/**
 * useEmailViewMode.js · v2.7.45+E13 (Feb-2026)
 * ─────────────────────────────────────────────────────────────────────
 * Hybrid view-mode preference hook (localStorage + DB).
 *
 *   • localStorage is the immediate source of truth — the layout snaps
 *     to the user's last choice with zero round-trip on every page load.
 *   • In the background we fetch `/admin/me/profile` once and override
 *     the local value when the server holds a different (more recent)
 *     setting — this is the "cross-device sync" leg.
 *   • Any change made here writes to BOTH localStorage AND the server
 *     (best-effort, fire-and-forget so a slow API call doesn't lag the
 *     UI).
 *
 * Modes: "three-pane" (default) · "list" · "compact"
 */
import { useCallback, useEffect, useState } from "react";
import adminApi from "@/lib/adminApi";

const KEY = "rtg_email_view_mode_v1";
const VALID = ["three-pane", "list", "compact"];

function readLocal() {
  try {
    const v = localStorage.getItem(KEY);
    return VALID.includes(v) ? v : "three-pane";
  } catch {
    return "three-pane";
  }
}

export default function useEmailViewMode() {
  const [mode, setMode] = useState(readLocal);
  const [synced, setSynced] = useState(false);

  // One-shot DB sync — hydrate from server profile (server wins if
  // value diverges and is valid).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await adminApi.get("/admin/me/profile");
        const remote = r?.data?.email_view_mode;
        if (!cancelled && VALID.includes(remote) && remote !== mode) {
          try { localStorage.setItem(KEY, remote); } catch { /* noop */ }
          setMode(remote);
        }
      } catch { /* non-fatal */ }
      finally { if (!cancelled) setSynced(true); }
    })();
    return () => { cancelled = true; };
    // intentionally run only once on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const change = useCallback((next) => {
    if (!VALID.includes(next)) return;
    setMode(next);
    try { localStorage.setItem(KEY, next); } catch { /* noop */ }
    // Fire-and-forget DB sync — never blocks the UI
    adminApi.post("/admin/me/preferences", { email_view_mode: next }).catch(() => {});
  }, []);

  return { mode, setMode: change, synced };
}
